import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { getQuarterOptions } from "@/lib/fiscal-year";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useCreateKeyResult } from "@/hooks/use-key-results";
import { useCurrentUser, isSupervisorOrAbove } from "@/hooks/use-current-user";
import { useUsers } from "@/hooks/use-users";
import { FormError } from "@/components/shared/form-error";
import { DatePicker } from "@/components/shared/date-picker";
import { compareUsersByLastName, formatNameLastFirst } from "@/lib/format-name";

const createKeyResultSchema = z.object({
  owner_id: z.string().min(1, "Owner is required"),
  title: z.string().min(1, "Title is required").max(500),
  description: z.string().max(5000),
  type: z.enum(["percentage", "binary", "metric"]),
  target_value: z.number().positive("Must be greater than 0").nullable().optional(),
  quarter: z.enum(["Q1", "Q2", "Q3", "Q4"]),
  due_date: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, "Must be YYYY-MM-DD")
    .nullable()
    .optional(),
});

type CreateKeyResultForm = z.infer<typeof createKeyResultSchema>;

interface CreateKeyResultDialogProps {
  objectiveId: string;
  objectiveOwnerId?: string;
  trigger?: React.ReactNode;
  year?: number;
}

export function CreateKeyResultDialog({ objectiveId, objectiveOwnerId, trigger, year }: CreateKeyResultDialogProps) {
  const [open, setOpen] = useState(false);
  const { currentUser } = useCurrentUser();
  const createKeyResult = useCreateKeyResult();
  const showOwnerPicker = isSupervisorOrAbove(currentUser?.role);
  const { data: usersData } = useUsers(showOwnerPicker ? { limit: 100 } : { limit: 0 });
  const allUsers = usersData?.data ?? [];
  const [krType, setKrType] = useState<"percentage" | "binary" | "metric">("percentage");
  const [dueDate, setDueDate] = useState<string | null>(null);

  const currentQuarter = `Q${Math.ceil((new Date().getMonth() + 1) / 3)}` as "Q1" | "Q2" | "Q3" | "Q4";

  const {
    register,
    handleSubmit,
    setValue,
    setError,
    reset,
    formState: { errors },
  } = useForm<CreateKeyResultForm>({
    resolver: zodResolver(createKeyResultSchema),
    defaultValues: {
      owner_id: objectiveOwnerId ?? currentUser?.id ?? "",
      title: "",
      description: "",
      type: "percentage",
      target_value: null,
      quarter: currentQuarter,
      due_date: null,
    },
  });

  const onSubmit = (data: CreateKeyResultForm) => {
    if (!currentUser) return;
    if (data.type === "metric" && (data.target_value == null || data.target_value <= 0)) {
      setError("target_value", { message: "Target value is required for metric type" });
      return;
    }
    createKeyResult.mutate(
      {
        ...data,
        objective_id: objectiveId,
        owner_id: data.owner_id,
        target_value: data.type === "metric" ? data.target_value : null,
        due_date: data.due_date ?? null,
      },
      {
        onSuccess: () => {
          setOpen(false);
          reset();
          setKrType("percentage");
          setDueDate(null);
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger ?? <Button variant="outline">Add Key Result</Button>}
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Add Key Result</DialogTitle>
          <DialogDescription>
            Define a measurable outcome for this objective. It will be created in draft status.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {showOwnerPicker && (
            <div className="space-y-2">
              <Label>Owner</Label>
              <Select
                defaultValue={objectiveOwnerId ?? currentUser?.id ?? ""}
                onValueChange={(v) => setValue("owner_id", v)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select owner" />
                </SelectTrigger>
                <SelectContent>
                  {[...allUsers].sort(compareUsersByLastName).map((u) => (
                    <SelectItem key={u.id} value={u.id}>{formatNameLastFirst(u.name)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="kr-title">Title</Label>
            <Input
              id="kr-title"
              placeholder="e.g., Reduce mean time to resolution to under 2 hours"
              {...register("title")}
            />
            <FormError message={errors.title?.message} />
          </div>

          <div className="space-y-2">
            <Label htmlFor="kr-description">Description</Label>
            <Textarea
              id="kr-description"
              placeholder="How will this be measured?"
              rows={3}
              {...register("description")}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Type</Label>
              <Select
                defaultValue="percentage"
                onValueChange={(v) => {
                  const typed = v as "percentage" | "binary" | "metric";
                  setValue("type", typed);
                  setKrType(typed);
                  if (typed !== "metric") {
                    setValue("target_value", null);
                  }
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="percentage">Percentage</SelectItem>
                  <SelectItem value="binary">Binary</SelectItem>
                  <SelectItem value="metric">Metric</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Quarter</Label>
              <Select
                defaultValue={currentQuarter}
                onValueChange={(v) => setValue("quarter", v as "Q1" | "Q2" | "Q3" | "Q4")}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {getQuarterOptions(year).map((q) => (
                    <SelectItem key={q.value} value={q.value}>{q.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {krType === "metric" && (
            <div className="space-y-2">
              <Label htmlFor="target-value">Target Value</Label>
              <Input
                id="target-value"
                type="number"
                step="any"
                placeholder="e.g., 100"
                {...register("target_value", { valueAsNumber: true })}
              />
              <FormError message={errors.target_value?.message} />
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="kr-due-date">Due date (optional)</Label>
            <DatePicker
              id="kr-due-date"
              value={dueDate}
              onChange={(value) => {
                setDueDate(value);
                setValue("due_date", value);
              }}
            />
            <FormError message={errors.due_date?.message} />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={createKeyResult.isPending}>
              {createKeyResult.isPending ? "Creating..." : "Create"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
